import { useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthSession } from '../../lib/authSession'
import { documentReaderHref } from '../../lib/documentReader'
import SourceDocumentList from '../documents/SourceDocumentList'
import DocumentLink from '../documents/DocumentLink'
import { recordHref } from '../navigation/context'
import { errorText } from '../recordEdits/api'
import {
  MATCH_FIELDS,
  matchingCommand,
  matchingText,
  type CapitalMatchingReview,
  type CapitalMatchCommand,
  type CapitalMatchReceipt,
} from './capitalMatching'
import { submitCapitalMatch } from './bookWorkApi'
import { createBookRequestScope } from './bookWorkLifecycle'
import type { BookReviewScope } from './bookReviewQuery'
import './book-review.css'

interface Props {
  review: CapitalMatchingReview
  scope: BookReviewScope
  onRefresh?: () => void
  /** Refresh parent counts without replacing this completed review/receipt. */
  onApplied?: () => void
  onPendingChange?: (pending: boolean) => void
}
export default function CapitalMatchingPane(props: Props) {
  const session = useAuthSession()
  return (
    <MatchingPane
      key={`${session}:${props.review.group_id}:${props.review.proposal_id}:${props.review.reviewed_binding}:${props.scope.fundId ?? ''}:${props.scope.clientId ?? ''}`}
      {...props}
    />
  )
}
function MatchingPane({ review, scope, onRefresh, onApplied, onPendingChange }: Props) {
  const location = useLocation()
  const [choice, setChoice] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [receipt, setReceipt] = useState<CapitalMatchReceipt | null>(null)
  const inFlight = useRef(false)
  const [requestScope] = useState(createBookRequestScope)
  const pendingCallback = useRef(onPendingChange)
  const appliedCallback = useRef(onApplied)
  pendingCallback.current = onPendingChange
  appliedCallback.current = onApplied
  useEffect(() => {
    requestScope.mount()
    return () => {
      requestScope.dispose()
      inFlight.current = false
      pendingCallback.current?.(false)
    }
  }, [requestScope])
  const intent = useRef<{ choice: string; body: CapitalMatchCommand } | null>(null)
  const returnTo = location.pathname + location.search
  const sourceDocument = review.documents.find((item) => item.document_id === review.document_id)
  const submit = async () => {
    if (inFlight.current) return
    const request = requestScope.begin()
    if (!request.current()) return
    inFlight.current = true
    setBusy(true)
    setError(null)
    onPendingChange?.(true)
    try {
      if (!intent.current || intent.current.choice !== choice)
        intent.current = { choice, body: matchingCommand(review, choice, crypto.randomUUID()) }
      const result = await submitCapitalMatch(review, intent.current.body)
      if (request.current()) {
        setReceipt(result)
        appliedCallback.current?.()
      }
    } catch (failure) {
      if (request.current()) setError(errorText(failure))
    } finally {
      if (request.current()) {
        inFlight.current = false
        setBusy(false)
        onPendingChange?.(false)
      }
    }
  }
  const door = receipt
    ? recordHref(
        scope.fundId
          ? { kind: 'fund', fund_id: scope.fundId }
          : scope.clientId
            ? { kind: 'client', client_id: scope.clientId }
            : { kind: 'all' },
        { focus: `trade:${receipt.trade_id}` }
      )
    : null
  return (
    <section
      className="book-record-comparison"
      data-testid="capital-matching-review"
      data-proposal-id={review.proposal_id}
    >
      <p className="book-record-comparison__sentence">
        {sourceDocument ? (
          <DocumentLink document={sourceDocument} context={{ returnTo }} />
        ) : (
          'This document'
        )}{' '}
        may describe an existing {review.instrument.name} movement of {review.holder.name}.
      </p>
      <div className="book-record-comparison__objects">
        <section className="book-record-comparison__object" data-object-kind="trade">
          <header className="book-record-comparison__object-header">
            <div>
              <h3>Movement comparison</h3>
              <p>Issuer · {review.issuer.name}</p>
            </div>
          </header>
          <div style={{ overflowX: 'auto' }}>
            <div
              className="book-record-comparison__columns"
              role="table"
              aria-label="Movement comparison"
            >
              <div className="book-record-comparison__head" role="row">
                {[
                  'Field',
                  'Current Record',
                  'Document states',
                  receipt ? 'Recorded result' : 'Proposed Record',
                ].map((label) => (
                  <span key={label} role="columnheader">
                    {label}
                  </span>
                ))}
              </div>
              {MATCH_FIELDS.filter(
                ([key]) =>
                  review.proposed[key] != null ||
                  review.candidates.some((item) => item.values[key] != null) ||
                  receipt?.recorded_values?.[key] != null
              ).map(([key, label, format]) => (
                <div className="book-record-comparison__row" role="row" key={key}>
                  <span className="book-record-comparison__field-label" role="cell">
                    {label}
                  </span>
                  <span className="book-record-comparison__value" role="cell">
                    {review.candidates.map((item, index) => (
                      <span key={item.trade_id}>
                        {review.candidates.length > 1 ? `${index + 1}. ` : ''}
                        {matchingText(item.values[key], format)}
                      </span>
                    ))}
                  </span>
                  <span className="book-record-comparison__value" role="cell">
                    {review.source_assertions[key]?.length
                      ? review.source_assertions[key]!.map((source, index) => (
                          <a
                            key={`${source.artifact_id}:${source.source_key}:${source.revision}:${index}`}
                            className="book-record-comparison__source-link"
                            href={documentReaderHref(source.document.document_id, {
                              proposalId: review.proposal_id,
                              annotationId: source.annotation_id,
                              page: source.page,
                              returnTo,
                            })}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={source.quote ?? undefined}
                          >
                            {matchingText(
                              source.value.state === 'value' ? source.value.value : null,
                              format
                            )}
                          </a>
                        ))
                      : 'Not stated'}
                  </span>
                  <span
                    className="book-record-comparison__value book-record-comparison__value--proposed"
                    role="cell"
                  >
                    {matchingText(
                      receipt ? receipt.recorded_values?.[key] : review.proposed[key],
                      format
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
          {review.candidates.map((item, index) => (
            <div key={item.trade_id} className="book-record-comparison__row">
              <strong>
                {review.candidates.length > 1
                  ? `Recorded movement ${index + 1}`
                  : 'Recorded movement'}
                {item.manual_entry ? ' · officer entered' : ''}
              </strong>
              <SourceDocumentList documents={[...item.documents]} context={{ returnTo }} />
            </div>
          ))}
          {!receipt ? (
            <div className="book-record-comparison__row">
              <fieldset
                disabled={busy || !review.can_submit}
                style={{ border: 0, padding: 0, margin: 0 }}
              >
                <legend>Choose the economic relationship.</legend>
                {review.candidates.map((item, index) => (
                  <label key={item.trade_id} style={{ display: 'block', padding: '8px 0' }}>
                    <input
                      type="radio"
                      name={`match-${review.proposal_id}`}
                      value={item.trade_id}
                      checked={choice === item.trade_id}
                      onChange={() => setChoice(item.trade_id)}
                    />{' '}
                    {review.candidates.length === 1
                      ? 'Same movement'
                      : `Same as recorded movement ${index + 1}`}
                  </label>
                ))}
                <label style={{ display: 'block', padding: '8px 0' }}>
                  <input
                    type="radio"
                    name={`match-${review.proposal_id}`}
                    value="new"
                    checked={choice === 'new'}
                    onChange={() => setChoice('new')}
                  />{' '}
                  Separate movement
                </label>
              </fieldset>
              {!review.can_submit ? (
                <p role="status">
                  Book the required earlier records before resolving this movement.
                </p>
              ) : null}
              {error ? <p role="alert">{error}</p> : null}
              <button
                className="vbtn primary"
                type="button"
                disabled={busy || !choice || !review.can_submit}
                onClick={submit}
              >
                {busy ? 'Recording…' : 'Record matching choice'}
              </button>
              {error ? (
                <button className="vbtn" type="button" onClick={onRefresh} disabled={busy}>
                  Refresh review
                </button>
              ) : null}
            </div>
          ) : (
            <div
              className="book-record-comparison__row"
              role="status"
              data-testid="capital-matching-receipt"
            >
              <strong>
                {receipt.outcome === 'same_trade'
                  ? 'One movement retained; the document is attached.'
                  : 'A separate movement is recorded; the original remains.'}
              </strong>
              {door ? (
                <p>
                  <Link to={door}>View recorded movement ›</Link>
                </p>
              ) : null}
              <button className="vbtn" type="button" onClick={onRefresh}>
                Refresh workbench
              </button>
            </div>
          )}
        </section>
      </div>
      <SourceDocumentList documents={[...review.documents]} context={{ returnTo }} />
    </section>
  )
}
